import React, { useState, useEffect, useRef, useMemo } from 'react'; // Import useMemo
import './App.css';
// Remove ThemeToggle import from here
// import ThemeToggle from './components/ThemeToggle';
import LandingText from './components/LandingText';
import ScrollDownButton from './components/ScrollDownButton';
// Remove unused icon imports
// import { SiPython, SiCplusplus } from 'react-icons/si';
// Import the new AboutMe component
import AboutMe from './components/AboutMe';
// Import the new WorkExperience component
import WorkExperience from './components/WorkExperience';
// Import the new Studies component
import Studies from './components/Studies';
// Import the new Skills component
import Skills from './components/Skills';
// Add new import for FloatingTabBar
import FloatingTabBar from './components/FloatingTabBar';

// Import icons from react-icons
import { FaUser, FaBriefcase, FaGraduationCap, FaTools } from 'react-icons/fa';

// Add a new CSS import for the content sections
import './AppContent.css';

function App() {
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme;
    }
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });
  const [isScrolled, setIsScrolled] = useState(false);
  // Use a ref to track if the scroll has already been triggered
  const scrollTriggeredRef = useRef(false);
  // Add state for active section
  const [activeSection, setActiveSection] = useState(''); // Initialize with an empty string or first section ID

  // Define sections for the FloatingTabBar
  const appSections = useMemo(() => [ // Wrap in useMemo
    { id: 'about-me', label: 'About', icon: <FaUser /> },
    { id: 'work-experience', label: 'Experience', icon: <FaBriefcase /> },
    { id: 'studies', label: 'Education', icon: <FaGraduationCap /> },
    { id: 'skills', label: 'Skills', icon: <FaTools /> },
  ], []); // Empty dependency array, as sections are static

  // Effect for theme changes
  useEffect(() => {
    document.body.classList.remove('light', 'dark');
    document.body.classList.add(theme);
    localStorage.setItem('theme', theme);
  }, [theme]); // This effect ONLY handles theme application

  // Effect for scroll listener (for initial scroll detection)
  useEffect(() => {
    const handleScroll = () => {
      // Check scroll position and if the trigger ref is false
      if (!scrollTriggeredRef.current && window.scrollY > 5) {
        setIsScrolled(true);
        scrollTriggeredRef.current = true; // Mark as triggered
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Cleanup: Remove listener on component unmount
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []); // Empty dependency array: run only once on mount

  // Effect for scroll spying to update activeSection
  useEffect(() => {
    if (!isScrolled) return; // Only run if content is visible

    // More robust way to determine active section:
    // Find the section that is closest to the top of the viewport (e.g., 100px from top)
    const handleActiveSectionScroll = () => {
      let currentSectionId = activeSection;
      const headerOffset = 80; // Adjust based on your sticky header or desired offset

      for (const section of appSections) {
        const element = document.getElementById(section.id);
        if (element) {
          const rect = element.getBoundingClientRect();
          // Check if the top of the section is at or above the offset and
          // the bottom of the section is below the offset (meaning it's in view near the top)
          // Or, if it's the first section and we are near the top of the page
          if (rect.top <= headerOffset && rect.bottom >= headerOffset) {
            currentSectionId = section.id;
            break; // Found the topmost visible section
          }
          // Fallback for the first section if scrolled to the very top
          if (section.id === appSections[0].id && window.scrollY < rect.height / 2) {
            currentSectionId = section.id;
          }
        }
      }
      if (activeSection !== currentSectionId) {
        setActiveSection(currentSectionId);
      }
    };


    window.addEventListener('scroll', handleActiveSectionScroll);
    // Initial check in case a section is already in view on load (e.g. due to hash URL)
    handleActiveSectionScroll();


    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleActiveSectionScroll);
    };
  }, [isScrolled, appSections, activeSection]); // Re-run if isScrolled or appSections change


  // Effect to scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []); // Empty dependency array ensures this runs only once on mount

  const toggleTheme = () => {
    setTheme(prevTheme => (prevTheme === 'light' ? 'dark' : 'light'));
  };

  const triggerScrollState = () => {
    // Directly set the state and update the ref if not already triggered
    if (!scrollTriggeredRef.current) {
        setIsScrolled(true);
        scrollTriggeredRef.current = true; // Mark as triggered
        // Scroll down slightly to ensure the visual change happens smoothly
        window.scrollTo({
            top: 20, // Scroll just enough to be > 10
            behavior: 'smooth'
        });
        // Set the initial active section when content appears
        if (appSections.length > 0) {
            setActiveSection(appSections[0].id);
        }
    }
  };

  const handleTabClick = (sectionId) => {
    setActiveSection(sectionId);
    // Scrolling is handled by the FloatingTabBar component itself
  };

  return (
    <div className={`App ${isScrolled ? 'content-scrolled' : ''}`}>
      {/* Remove ThemeToggle from here */}
      {/* <ThemeToggle theme={theme} toggleTheme={toggleTheme} isScrolled={isScrolled} /> */}
      {/* Pass theme and toggleTheme to LandingText */}
      <LandingText isScrolled={isScrolled} theme={theme} toggleTheme={toggleTheme} />
      {/* Only show button if not scrolled */}
      {!isScrolled && <ScrollDownButton onScrollClick={triggerScrollState} />}

      {/* Conditionally render the content sections and FloatingTabBar */}
      {isScrolled && (
        <>
          <main className="app-content">
            {/* Pass id to section components */}
            <AboutMe id="about-me" />
            <WorkExperience id="work-experience" />
            <Studies id="studies" />
            <Skills id="skills" />
          </main>
          <FloatingTabBar
            sections={appSections}
            activeSection={activeSection}
            onTabClick={handleTabClick}
          />
        </>
      )}
    </div>
  );
}

export default App;
